import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Row,
  Col,
  DatePicker,
  Select,
  Switch,
  Button,
  Input,
  Typography,
  Empty,
  Alert,
  Result,
  Divider,
} from "antd";
import { SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { Header } from "../components";
import { ThreeDot } from "react-loading-indicators";
import { fetchOrders } from "../utils/fetchOrders";
import { fetchDrivers } from "../utils/fetchDrivers";
import { fetchAllDriverSummaries } from "../utils/fetchDeliveries";
import { fromDatetoYearMonthDay } from "../utils/utils";
import { groupOrdersByDriver, computeDeliveryCounts } from "../utils/routeMonitoring";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

import RouteMonitoringSummary from "../components/RouteMonitoring/RouteMonitoringSummary";
import DriverRouteCard from "../components/RouteMonitoring/DriverRouteCard";
import UnassignedOrdersAlert from "../components/RouteMonitoring/UnassignedOrdersAlert";

const fetchOrdersAndDrivers = async (routeDay) => {
  // `short=true` returned 0 orders for any date once tried against the real
  // API (nobody else in the codebase uses that flag, so it was unverified) —
  // back to the full payload until that's sorted out on the backend.
  const [orders, drivers] = await Promise.all([
    fetchOrders(null, routeDay),
    fetchDrivers(),
  ]);
  return { orders, drivers };
};

// Bulk endpoint returns one entry per driver with at least $1 collected that
// day, keyed here by driver_id so DriverRouteCard can look itself up directly.
const fetchDriverSummariesByDriver = async (routeDay) => {
  const summaries = await fetchAllDriverSummaries(routeDay);
  return Object.fromEntries(summaries.map((s) => [s.driver_id, s]));
};

const RouteMonitoring = () => {
  const navigate = useNavigate();
  const { routeDay } = useParams();
  const { user } = useAuth();
  const canReadPayments = hasPermission(user?.permissions, "deliveries:read");

  const [lastUpdated, setLastUpdated] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(true);
  // "continuous" fires the next request as soon as the previous one resolves,
  // instead of waiting out a fixed interval — the default, for a genuinely
  // live view; the fixed intervals are there for lighter polling if needed.
  const [refreshMode, setRefreshMode] = useState("continuous");
  const [search, setSearch] = useState("");

  const isContinuous = refreshMode === "continuous";

  const hasRouteDay = !!routeDay && routeDay !== "undefined";

  // Default straight to today so this reads as a live monitoring view, not a
  // "pick a date" flow like DashboardDailyRoute.
  useEffect(() => {
    if (!hasRouteDay) {
      navigate(`/route-monitoring/${fromDatetoYearMonthDay(new Date())}`, {
        replace: true,
      });
    }
  }, [hasRouteDay, navigate]);

  // SWR's cache is keyed by routeDay and lives outside this component, so
  // clicking into an order and hitting back re-mounts this page but SWR
  // serves the last-known data instantly (no spinner) while it revalidates
  // in the background instead of blocking on a full refetch.
  const {
    data: ordersData,
    error,
    isLoading,
    isValidating: isValidatingOrders,
    mutate: revalidateOrders,
  } = useSWR(hasRouteDay ? ["route-monitoring", routeDay] : null, () => fetchOrdersAndDrivers(routeDay), {
    // In continuous mode the loop below drives refetching by calling mutate()
    // back-to-back, so SWR's own timer-based polling is disabled here.
    refreshInterval: autoRefresh && !isContinuous ? refreshMode * 1000 : 0,
    revalidateOnFocus: true,
    onSuccess: () => setLastUpdated(new Date()),
  });

  const orders = ordersData?.orders ?? [];
  const drivers = ordersData?.drivers ?? [];

  const groups = groupOrdersByDriver(orders, routeDay || "");

  // Single bulk request for every driver's summary that day, instead of one
  // request per driver — independent of the orders query, so it can fire in
  // parallel rather than waiting on `groups` to know which drivers to ask for.
  const {
    data: driverSummaries = {},
    error: summaryError,
    isLoading: isSummaryLoading,
    isValidating: isValidatingSummaries,
    mutate: revalidateSummaries,
  } = useSWR(
    hasRouteDay && canReadPayments ? ["route-monitoring-summaries", routeDay] : null,
    () => fetchDriverSummariesByDriver(routeDay),
    {
      refreshInterval: autoRefresh && !isContinuous ? refreshMode * 1000 : 0,
      revalidateOnFocus: true,
    },
  );

  // Continuous mode: as soon as one round of requests resolves, fire the next
  // — no fixed delay. Falls back to a brief pause only when a request fails,
  // so a persistent outage doesn't turn into a tight, request-per-millisecond loop.
  useEffect(() => {
    if (!autoRefresh || !isContinuous || !hasRouteDay) return;
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        try {
          await Promise.all([revalidateOrders(), revalidateSummaries()]);
        } catch {
          if (!cancelled) await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    };
    loop();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, isContinuous, hasRouteDay, routeDay]);

  // Ticks once a second only to keep the "actualizado hace Ns" label live.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const overallCounts = computeDeliveryCounts(orders, routeDay || "");

  const aggregatedRecaudacion = Object.values(driverSummaries).flatMap(
    (s) => s.recaudacion || [],
  );
  const aggregatedTotal = Object.values(driverSummaries).reduce(
    (acc, s) => acc + (s.total || 0),
    0,
  );

  // personnel_id 0 is the backend's "No asignado" sentinel, not a real driver
  // — its orders live under the `null` key in `groups` (see groupOrdersByDriver)
  // and are shown in UnassignedOrdersAlert instead of the driver grid.
  const unassignedOrders = groups.get(null) || [];

  const driverCards = drivers
    .filter((d) => d.personnel_id !== 0 && groups.has(d.personnel_id))
    .filter((d) => d.name.toLowerCase().includes(search.toLowerCase()));

  const secondsAgo = lastUpdated ? Math.max(0, Math.round((now - lastUpdated.getTime()) / 1000)) : null;
  const isRefreshing = isValidatingOrders || isValidatingSummaries;
  const hasData = !!ordersData;
  const hasError = !!error || !!summaryError;

  const handleManualRefresh = () => {
    revalidateOrders();
    revalidateSummaries();
  };

  if (!hasRouteDay) return null;

  return (
    <div className="">
      <div className="mb-5 flex flex-wrap justify-between items-center gap-3">
        <Header category="Ruta diaria" title="Monitoreo de Rutas" />
        <div className="flex flex-wrap items-center gap-3">
          <DatePicker
            value={dayjs(routeDay)}
            allowClear={false}
            onChange={(_, dateString) => {
              if (dateString) navigate(`/route-monitoring/${dateString}`);
            }}
          />
          <Switch checked={autoRefresh} onChange={setAutoRefresh} />
          <Select
            value={refreshMode}
            onChange={setRefreshMode}
            disabled={!autoRefresh}
            options={[
              { value: "continuous", label: "Continuo" },
              { value: 15, label: "15s" },
              { value: 30, label: "30s" },
              { value: 60, label: "60s" },
            ]}
            style={{ width: 100 }}
          />
          <Typography.Text type={hasError ? "warning" : "secondary"} className="text-xs">
            {hasError
              ? "Sin actualizar — reintentando"
              : secondsAgo !== null
                ? `Actualizado hace ${secondsAgo}s`
                : ""}
          </Typography.Text>
          <Button
            icon={<SyncOutlined spin={isRefreshing} />}
            onClick={handleManualRefresh}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="w-full text-center min-h-72 flex items-center justify-center">
          <ThreeDot variant="brick-stack" color="#4048F1" size="medium" text="" textColor="" />
        </div>
      ) : hasError && !hasData ? (
        <Result
          status="error"
          title="No se pudo cargar la información de rutas"
          subTitle="Revisa tu conexión e intenta nuevamente."
          extra={
            <Button type="primary" onClick={handleManualRefresh}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <>
          {hasError && (
            <Alert
              className="mb-4"
              type="warning"
              showIcon
              message="No se pudo actualizar la información"
              description={`Mostrando los últimos datos disponibles${
                secondsAgo !== null ? ` (hace ${secondsAgo}s)` : ""
              }. Reintentando automáticamente.`}
              action={
                <Button size="small" onClick={handleManualRefresh}>
                  Reintentar ahora
                </Button>
              }
            />
          )}

          <RouteMonitoringSummary
            counts={overallCounts}
            recaudacion={aggregatedRecaudacion}
            total={aggregatedTotal}
            loadingSummary={canReadPayments && isSummaryLoading}
          />

          <UnassignedOrdersAlert orders={unassignedOrders} routeDay={routeDay} />

          <Input.Search
            placeholder="Buscar chofer..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
            className="mb-4"
          />

          {driverCards.length === 0 && unassignedOrders.length === 0 ? (
            <Empty description="Sin pedidos para esta fecha" />
          ) : driverCards.length === 0 ? null : (
            <>
              <Divider orientation="left" plain>
                Choferes
              </Divider>
              <Row gutter={[16, 16]}>
                {driverCards.map((driver) => (
                  <Col xs={24} lg={12} xl={8} key={driver.personnel_id}>
                    <DriverRouteCard
                      driver={driver}
                      orders={groups.get(driver.personnel_id) || []}
                      routeDay={routeDay}
                      summary={driverSummaries[driver.personnel_id]}
                      summaryLoading={canReadPayments && isSummaryLoading}
                    />
                  </Col>
                ))}
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RouteMonitoring;
